import clsx from 'clsx';
import { AlertTriangle, Building2, ExternalLink, History, MapPin, Pencil, Repeat, RotateCcw, Search, Send, Trash2, Undo2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, BILLING_TYPES, daysOnWeekdays, isoWeekday, parseHoursInput, weekStart, type BillingType } from '@pz/core';
import type { EntryView, ProcOutput, TaskView } from '@pz/server';
import { useCategories } from '../lib/categories';
import { projectColorVar } from '../lib/colors';
import { h, hh, d, dt, wd } from '../lib/format';
import { useDebounced, useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { Button, Checkbox, ConfirmModal, ErrorBox, Field, IconButton, Input, Loading, matchesSearch, Modal, Notice, Pill, Select, StatusPill, Textarea, useToast } from './ui';
import { FavoriteStar, MAX_DAYS, PlanReview, useFavorites, useReview } from './quick';

export interface PickedTask {
  id: string;
  ref: string;
  title: string;
  projectNumber: string;
  projectName: string;
  defaultBillingType: BillingType;
  billingTypeFixed: boolean;
  formaState: string | null;
  statusGroup: string;
  formaIssueId?: string | null;
  formaProjectId?: string | null;
  source?: string;
  locationPath?: string | null;
  manualCategory?: string | null;
  plannedMinutes?: number | null;
  /** Forma category and type: "Coordination › Clash" */
  formaKind?: string | null;
  projectColor?: string | null;
  projectInternal?: boolean;
}

/** "Category › Type" of a Forma task (null for platform tasks). */
export function formaKindText(category: string | null | undefined, type: string | null | undefined) {
  if (!category) return null;
  return type ? `${category} › ${type}` : category;
}

export function toPicked(x: TaskView): PickedTask {
  return {
    id: x.id,
    ref: x.ref,
    title: x.title,
    projectNumber: x.projectNumber,
    projectName: x.projectName,
    defaultBillingType: x.defaultBillingType,
    billingTypeFixed: x.billingTypeFixed,
    formaState: x.formaState,
    statusGroup: x.statusGroup,
    formaIssueId: x.formaIssueId,
    formaProjectId: x.formaProjectId,
    source: x.source,
    locationPath: x.locationPath,
    manualCategory: x.manualCategory,
    plannedMinutes: x.plannedMinutes,
    formaKind: x.source === 'forma' ? formaKindText(x.issueType, x.issueSubtype) : null,
    projectColor: x.projectColor,
    projectInternal: x.projectInternal,
  };
}

export function formaLink(template: string, formaProjectId?: string | null, issueId?: string | null) {
  if (!formaProjectId || !issueId || !template) return null;
  return template.replace('{projectId}', encodeURIComponent(formaProjectId)).replace('{issueId}', encodeURIComponent(issueId));
}

/**
 * Link that opens the issue in Autodesk Forma (new tab). The demo has no real Forma projects behind its issues, so
 * there the link explains what it does instead of opening a page that does not exist.
 */
export function FormaIssueLink({ href, className, title, children }: { href: string; className?: string; title?: string; children: ReactNode }) {
  const { t } = useTranslation();
  const toast = useToast();
  if (__DEMO__)
    return (
      <button type="button" className={className} title={title} onClick={() => toast.info(t('demo.formaLink'))}>
        {children}
      </button>
    );
  return (
    <a href={href} target="_blank" rel="noreferrer" className={className} title={title}>
      {children}
    </a>
  );
}

/**
 * Small labels of a task: its kind (Manuell, Intern; Forma issues carry their "#" number already) and the states that
 * need attention. The kind is a word with an icon, not a colour: colours belong to the projects (request 13).
 */
export function TaskBadges({ formaState, statusGroup, source, internal, deleted }: { formaState?: string | null; statusGroup?: string; source?: string; internal?: boolean; deleted?: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      {deleted && (
        <Pill tone="warn" title={t('task.deletedHint')}>
          <AlertTriangle className="size-3" />
          {t('task.deleted')}
        </Pill>
      )}
      {internal ? (
        <Pill>
          <Building2 className="size-3" />
          {t('task.internal')}
        </Pill>
      ) : (
        source === 'manual' && <Pill>{t('task.manual')}</Pill>
      )}
      {formaState === 'deleted' && (
        <Pill tone="warn">
          <AlertTriangle className="size-3" />
          {t('task.formaDeleted')}
        </Pill>
      )}
      {formaState === 'access_lost' && (
        <Pill tone="warn">
          <AlertTriangle className="size-3" />
          {t('task.formaAccessLost')}
        </Pill>
      )}
      {statusGroup === 'closed' && <Pill>{t('task.closed')}</Pill>}
      {statusGroup === 'done' && <Pill>{t('task.done')}</Pill>}
    </>
  );
}

/** Colour dot of a project (nothing for a project without colour); always next to the project number or name. */
export function ProjectDot({ color, className }: { color?: string | null; className?: string }) {
  const c = projectColorVar(color);
  if (!c) return null;
  return <span aria-hidden className={clsx('inline-block size-2 shrink-0 rounded-full align-middle', className)} style={{ background: c }} />;
}

/** Second line of a task: project, location, Forma category and type or platform category, plan. */
export function TaskMeta({ task, showProject = true }: { task: PickedTask; showProject?: boolean }) {
  const { t } = useTranslation();
  const cats = useCategories(task.source === 'manual');
  const parts: string[] = [];
  if (task.source === 'manual' && task.manualCategory) parts.push(cats.label(task.manualCategory));
  return (
    <span className="flex flex-wrap items-center gap-x-2 text-xs text-graphite">
      {showProject && (
        <span className="inline-flex items-center gap-1">
          <ProjectDot color={task.projectColor} />
          {task.projectNumber} {task.projectName}
        </span>
      )}
      {parts.map((p, i) => (
        <span key={i}>{p}</span>
      ))}
      {task.locationPath && (
        <span className="inline-flex items-center gap-0.5">
          <MapPin className="size-3" aria-hidden />
          {task.locationPath}
        </span>
      )}
      {task.formaKind && <span>{task.formaKind}</span>}
      {!!task.plannedMinutes && <span className="num">{t('picker.plan', { hours: h(task.plannedMinutes, 0) })}</span>}
    </span>
  );
}

type BookableProject = ProcOutput<'tasks.projects'>['projects'][number];
type LocationNode = ProcOutput<'tasks.locations'>['nodes'][number];

/** Searchable project list: number, name in Projektzeit and name in Forma (hundreds of projects). */
export function ProjectCombobox({
  projects,
  value,
  onChange,
  allLabel,
  className,
  clearable = true,
  id,
}: {
  projects: { id: string; number: string; name: string; formaName?: string | null; hint?: string }[];
  value: string;
  onChange: (id: string) => void;
  allLabel: string;
  className?: string;
  /** false: a project must stay chosen (no "all" entry, no clear button) */
  clearable?: boolean;
  id?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const selected = projects.find((p) => p.id === value);
  const list = useMemo(() => {
    const hits = text.trim() ? projects.filter((p) => matchesSearch(`${p.number} ${p.name} ${p.formaName ?? ''}`, text)) : projects;
    return hits.slice(0, 80);
  }, [projects, text]);
  const options = [...(clearable ? [{ id: '', number: '', name: allLabel, formaName: null, hint: undefined as string | undefined }] : []), ...list];
  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
    setText('');
  };
  return (
    <div className={clsx('relative', className)}>
      <Input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-label={id ? undefined : t('common.project')}
        placeholder={allLabel}
        value={open ? text : selected ? `${selected.number} · ${selected.name}` : ''}
        onFocus={() => {
          setOpen(true);
          setActive(0);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => {
          setText(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(options.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && open) {
            e.preventDefault();
            pick(options[active]?.id ?? '');
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="pr-8"
      />
      {value && !open && clearable && (
        <button type="button" className="absolute top-2 right-2 rounded p-0.5 text-faint hover:text-ink" aria-label={t('common.reset')} onClick={() => onChange('')}>
          <X className="size-4" />
        </button>
      )}
      {open && (
        <ul role="listbox" className="absolute z-30 mt-1 max-h-72 w-full min-w-72 overflow-y-auto rounded-md border border-line bg-sheet py-1 shadow-lg">
          {options.map((p, i) => (
            <li key={p.id || 'all'} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(p.id);
                }}
                onMouseEnter={() => setActive(i)}
                className={clsx('flex w-full flex-col px-3 py-1.5 text-left text-sm', i === active ? 'bg-accent-soft' : 'hover:bg-sheet-2')}
              >
                {p.id ? (
                  <>
                    <span>
                      <span className="num font-semibold">{p.number}</span> {p.name}
                    </span>
                    {(p.formaName && p.formaName !== p.name) || p.hint ? (
                      <span className="text-xs text-graphite">{[p.formaName && p.formaName !== p.name ? `Forma: ${p.formaName}` : '', p.hint].filter(Boolean).join(' · ')}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-graphite">{p.name}</span>
                )}
              </button>
            </li>
          ))}
          {list.length === 0 && <li className="px-3 py-2 text-sm text-graphite">{t('common.noResults')}</li>}
        </ul>
      )}
    </div>
  );
}

/** Location (level 1) and sub-location (all levels below) of the Forma location tree. */
export function LocationSelects({
  nodes,
  top,
  sub,
  onChange,
}: {
  nodes: LocationNode[];
  top: string;
  sub: string;
  onChange: (top: string, sub: string) => void;
}) {
  const { t } = useTranslation();
  const tops = nodes.filter((n) => !n.parentId);
  const topNode = tops.find((n) => n.id === top);
  const below = useMemo(() => {
    if (!topNode) return [];
    const ids = new Set([topNode.id]);
    const out: LocationNode[] = [];
    for (const n of nodes) {
      if (n.parentId && ids.has(n.parentId)) {
        ids.add(n.id);
        out.push(n);
      }
    }
    return out;
  }, [nodes, topNode]);
  const rel = (n: LocationNode) => (topNode && n.path.startsWith(`${topNode.name} / `) ? n.path.slice(topNode.name.length + 3) : n.path);
  return (
    <>
      <Select className="min-w-40 flex-1" aria-label={t('picker.location')} value={top} onChange={(e) => onChange(e.target.value, '')}>
        <option value="">{t('picker.allLocations')}</option>
        {tops.map((n) => (
          <option key={n.id} value={n.id} disabled={n.taskCount === 0}>
            {n.name} ({n.taskCount})
          </option>
        ))}
      </Select>
      <Select className="min-w-40 flex-1" aria-label={t('picker.subLocation')} value={sub} disabled={!below.length} onChange={(e) => onChange(top, e.target.value)}>
        <option value="">{below.length ? t('picker.allSubLocations') : t('picker.noSubLocations')}</option>
        {below.map((n) => (
          <option key={n.id} value={n.id} disabled={n.taskCount === 0}>
            {rel(n)} ({n.taskCount})
          </option>
        ))}
      </Select>
    </>
  );
}

/**
 * Filter by Forma category or type next to the project selection: '' (all), 'c:<category>' or
 * 't:<category>\n<type>'. The list comes from the bookable open tasks (of the chosen project or of all projects,
 * grouped by title), with the number of tasks. Hidden when there is nothing to choose.
 */
export function FormaKindSelect({ value, onChange, projectId, personId, className }: { value: string; onChange: (v: string) => void; projectId?: string; personId?: string; className?: string }) {
  const { t } = useTranslation();
  const kinds = useQ('tasks.kinds', { personId, projectId: projectId || undefined });
  const categories = kinds.data?.categories ?? [];
  const values = useMemo(() => new Set(categories.flatMap((c) => [`c:${c.title}`, ...c.types.map((x) => `t:${c.title}\n${x.title}`)])), [categories]);
  useEffect(() => {
    // a type that the chosen project does not have is dropped
    if (value && kinds.data && !values.has(value)) onChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kinds.data, values]);
  if (!categories.length) return null;
  return (
    <Select className={className} value={value} onChange={(e) => onChange(e.target.value)} aria-label={t('tasks.formaKind')} title={t('tasks.formaKind')}>
      <option value="">{t('tasks.allKinds')}</option>
      {categories.map((c) => (
        <optgroup key={c.title} label={c.title}>
          <option value={`c:${c.title}`}>{t('tasks.kindCount', { kind: t('tasks.wholeCategory', { category: c.title }), count: c.count })}</option>
          {c.types.map((x) => (
            <option key={x.title} value={`t:${c.title}\n${x.title}`}>
              {t('tasks.kindCount', { kind: x.title, count: x.count })}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}

/** Input of tasks.search for the value of FormaKindSelect. */
export function formaKindInput(value: string): { formaCategory?: string; formaType?: string } {
  if (value.startsWith('c:')) return { formaCategory: value.slice(2) };
  if (value.startsWith('t:')) {
    const [formaCategory, formaType] = value.slice(2).split('\n');
    return { formaCategory, formaType };
  }
  return {};
}

export type SourceFilter = 'all' | 'forma' | 'manual';

export function SourceSwitch({ value, onChange }: { value: SourceFilter; onChange: (v: SourceFilter) => void }) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t('picker.source')} className="inline-flex shrink-0 rounded-md border border-line-strong p-0.5">
      {(['all', 'forma', 'manual'] as const).map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          onClick={() => onChange(s)}
          className={clsx('rounded px-2.5 py-1 text-[13px] font-medium whitespace-nowrap', value === s ? 'bg-accent text-accent-ink' : 'text-graphite hover:text-ink')}
        >
          {t(`picker.sources.${s}`)}
        </button>
      ))}
    </div>
  );
}

interface PickerFilter {
  source: SourceFilter;
  projectId: string;
}

function loadPickerFilter(): PickerFilter {
  try {
    const v = JSON.parse(localStorage.getItem('pz-picker') ?? '{}') as Partial<PickerFilter>;
    return { source: v.source === 'forma' || v.source === 'manual' ? v.source : 'all', projectId: typeof v.projectId === 'string' ? v.projectId : '' };
  } catch {
    return { source: 'all', projectId: '' };
  }
}

/**
 * Task selection for bookings: filter by source (Forma / own tasks), project (with Forma name),
 * location and sub-location from Forma, category for own tasks, plus search by number or title.
 * Without filters the recently used tasks come first. The last source and project are remembered.
 */
export function TaskPicker({ onPick, personId, autoFocus }: { onPick: (t: PickedTask) => void; personId?: string; autoFocus?: boolean }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<PickerFilter>(loadPickerFilter);
  const [top, setTop] = useState('');
  const [sub, setSub] = useState('');
  const [category, setCategory] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim());
  const projects = useQ('tasks.projects', { personId });
  const projectList = projects.data?.projects ?? [];
  const project = projectList.find((p) => p.id === filter.projectId);
  const showLocations = !!project?.hasLocations && filter.source !== 'manual';
  const locations = useQ('tasks.locations', { projectId: filter.projectId, personId }, { enabled: showLocations });
  const cats = useCategories(filter.source === 'manual');
  const kindActive = filter.source !== 'manual' && !!kind;
  const noFilter = filter.source === 'all' && !filter.projectId && !query && !kindActive;
  const recent = useQ('tasks.recent', {}, { enabled: !personId && noFilter });
  // favorites are the person's own: not offered while booking for somebody else
  const favs = useFavorites(!personId);
  const search = useQ('tasks.search', {
    query: query || undefined,
    personId,
    limit: 60,
    projectId: filter.projectId || undefined,
    source: filter.source === 'all' ? undefined : filter.source,
    locationId: showLocations ? sub || top || undefined : undefined,
    category: filter.source === 'manual' && category ? category : undefined,
    ...(kindActive ? formaKindInput(kind) : {}),
  });
  const [active, setActive] = useState(0);
  const update = (patch: Partial<PickerFilter>) => {
    const next = { ...filter, ...patch };
    setFilter(next);
    setActive(0);
    try {
      localStorage.setItem('pz-picker', JSON.stringify(next));
    } catch {
      /* storage not available */
    }
  };
  useEffect(() => {
    // a remembered project that is no longer bookable is dropped
    if (projects.data && filter.projectId && !project) update({ projectId: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.data]);
  useEffect(() => {
    setTop('');
    setSub('');
  }, [filter.projectId]);

  // without filters: favorites first, then the recently used tasks, then the rest (each task once)
  const { items, favCount, recentCount } = useMemo(() => {
    const fromSearch = (search.data?.tasks ?? []).map(toPicked);
    if (!noFilter || personId) return { items: fromSearch as PickedTask[], favCount: 0, recentCount: 0 };
    const fav = favs.tasks.map(toPicked);
    const seen = new Set(fav.map((x) => x.id));
    const rec = (recent.data ?? []).map(toPicked).filter((x) => !seen.has(x.id));
    for (const x of rec) seen.add(x.id);
    return { items: [...fav, ...rec, ...fromSearch.filter((x) => !seen.has(x.id))] as PickedTask[], favCount: fav.length, recentCount: rec.length };
  }, [search.data, recent.data, favs.tasks, noFilter, personId]);
  const groupAt = (i: number) => {
    if (favCount + recentCount === 0) return null;
    if (i === 0 && favCount > 0) return t('quick.favorites');
    if (i === favCount && recentCount > 0) return t('week.recent');
    if (i === favCount + recentCount) return t('tasks.title');
    return null;
  };
  const categoryOptions = cats.list.filter((c) => c.active);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <SourceSwitch value={filter.source} onChange={(source) => update({ source })} />
        <ProjectCombobox
          className="min-w-56 flex-1"
          allLabel={t('picker.allProjects')}
          value={filter.projectId}
          onChange={(projectId) => update({ projectId })}
          projects={projectList.map((p: BookableProject) => ({
            id: p.id,
            number: p.number,
            name: p.name,
            formaName: p.formaName,
            hint: t('picker.counts', { forma: p.formaTasks, manual: p.manualTasks }),
          }))}
        />
        {filter.source !== 'manual' && (
          <FormaKindSelect
            className="w-full sm:w-60"
            value={kind}
            projectId={filter.projectId}
            personId={personId}
            onChange={(v) => {
              setKind(v);
              setActive(0);
            }}
          />
        )}
      </div>
      {(showLocations || filter.source === 'manual') && (
        <div className="flex flex-wrap gap-2">
          {showLocations && locations.data && (
            <LocationSelects
              nodes={locations.data.nodes}
              top={top}
              sub={sub}
              onChange={(a, b) => {
                setTop(a);
                setSub(b);
                setActive(0);
              }}
            />
          )}
          {filter.source === 'manual' && (
            <Select className="min-w-56 flex-1" aria-label={t('tasks.category')} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('picker.allCategories')}</option>
              {categoryOptions.map((c) => (
                <option key={c.key} value={c.key}>
                  {cats.label(c.key)}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
      <div className="relative">
        <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-faint" />
        <Input
          autoFocus={autoFocus}
          className="pl-8"
          placeholder={t('entry.pickTask')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(items.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === 'Enter' && items[active]) {
              e.preventDefault();
              onPick(items[active]);
            }
          }}
        />
      </div>
      <ul className="max-h-72 overflow-y-auto rounded-md border border-line" role="listbox" aria-label={t('entry.task')}>
        {items.length === 0 && <li className="px-3 py-3 text-sm text-graphite">{search.isLoading ? t('app.loading') : t('common.noResults')}</li>}
        {items.map((x, i) => {
          const group = groupAt(i);
          return (
            <li key={x.id} role="option" aria-selected={i === active}>
              {group && <div className="bg-sheet-2 px-3 py-1 text-xs text-graphite">{group}</div>}
              <div onMouseEnter={() => setActive(i)} className={clsx('flex items-stretch border-b border-line/60', i === active ? 'bg-accent-soft' : 'hover:bg-sheet-2')}>
                <button type="button" onClick={() => onPick(x)} className="flex min-w-0 flex-1 flex-col gap-0.5 px-3 py-2 text-left">
                  <span className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="num font-semibold text-accent">{x.ref}</span>
                    <span className="font-medium">{x.title}</span>
                    <TaskBadges formaState={x.formaState} statusGroup={x.statusGroup} />
                  </span>
                  <TaskMeta task={x} />
                </button>
                {!personId && <FavoriteStar className="self-center" on={favs.ids.has(x.id)} disabled={favs.pending} onToggle={() => favs.toggle(x.id)} />}
              </div>
            </li>
          );
        })}
        {search.data?.more && <li className="px-3 py-2 text-xs text-graphite">{t('picker.more')}</li>}
      </ul>
    </div>
  );
}

export function HoursInput({ value, onChange, id, placeholder, className }: { value: number | null; onChange: (m: number | null) => void; id?: string; placeholder?: string; className?: string }) {
  const [text, setText] = useState(value ? h(value) : '');
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      setText(value ? h(value) : '');
      last.current = value;
    }
  }, [value]);
  return (
    <Input
      id={id}
      inputMode="decimal"
      className={clsx('num', className ?? 'w-28')}
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const m = parseHoursInput(e.target.value);
        last.current = m;
        onChange(m);
      }}
      onBlur={() => {
        const m = parseHoursInput(text);
        if (m) setText(h(m));
      }}
    />
  );
}

/** Work location choices from the settings; an old free-text value of an entry stays selectable. */
export function WorkLocationSelect({ value, onChange, id }: { value: string; onChange: (v: string) => void; id?: string }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const options = me.settings.workLocations;
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('entry.locationNone')}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
      {value && !options.includes(value) && <option value={value}>{value}</option>}
    </Select>
  );
}

/** Work locations named in brackets in the day texts of a Stundennachweis (company list plus any chosen before). */
export function LocationChecks({ value, onChange, options }: { value: string[]; onChange: (v: string[]) => void; options?: string[] }) {
  const { me } = useMe();
  const all = [...new Set([...(options ?? me.settings.workLocations), ...value])];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {all.map((l) => (
        <Checkbox key={l} label={l} checked={value.includes(l)} onChange={(e) => onChange(e.target.checked ? [...value, l] : value.filter((x) => x !== l))} />
      ))}
    </div>
  );
}

function lastLocation(options: string[]): string {
  try {
    const v = localStorage.getItem('pz-location');
    if (v !== null && (v === '' || options.includes(v))) return v;
  } catch {
    /* storage not available */
  }
  return options[0] ?? '';
}

function rememberLocation(v: string) {
  try {
    localStorage.setItem('pz-location', v);
  } catch {
    /* storage not available */
  }
}

/**
 * Hours to invoice for one entry. Empty = recorded hours. May be higher or lower than recorded
 * (e.g. value based billing); all other figures keep using the recorded hours.
 */
export function BilledDialog({ entry, onClose }: { entry: EntryView | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [billed, setBilled] = useState<number | null>(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (entry) {
      setBilled(entry.billedMinutes);
      setNote(entry.billingNote ?? '');
    }
  }, [entry]);
  const save = useM('time.setBilled', { invalidate: ['time.', 'approvals.', 'billing.', 'kpi.', 'cockpit.'] });
  if (!entry) return null;
  return (
    <Modal
      open={!!entry}
      onClose={onClose}
      title={t('entry.billedTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save
                .mutateAsync({ id: entry.id, billedMinutes: billed, billingNote: note || null })
                .then(() => {
                  toast.ok(t('common.saved'));
                  onClose();
                })
                .catch(() => {})
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="text-sm">
          <div>
            <span className="num font-semibold text-accent">{entry.taskRef}</span> {entry.taskTitle}
          </div>
          <div className="text-graphite">
            {entry.personName} · {d(entry.workDate)} · {t('entry.recorded', { hours: h(entry.minutes) })}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('entry.billedMinutes')} hint={t('entry.billedMinutesHint')}>
            <HoursInput value={billed} onChange={setBilled} placeholder={h(entry.minutes)} />
          </Field>
          <Button size="sm" variant="ghost" onClick={() => setBilled(null)}>
            {t('entry.billedReset')}
          </Button>
        </div>
        <Field label={t('entry.billingNote')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        <Notice tone="neutral">{t('entry.billedInfo')}</Notice>
        <ErrorBox error={save.error} />
      </div>
    </Modal>
  );
}

/** Compact display of invoiced hours with edit button (approvals, lists). */
export function BilledCell({ entry, onEdit }: { entry: EntryView; onEdit: (e: EntryView) => void }) {
  const { t } = useTranslation();
  const differs = entry.billedMinutes !== null && entry.billedMinutes !== entry.minutes;
  if (entry.billingType === 'non_billable' || (!entry.perm.editBilling && !differs)) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      {differs && (
        <span className={clsx('num rounded px-1 font-medium', (entry.billedMinutes ?? 0) > entry.minutes ? 'bg-accent-soft text-accent' : 'bg-sheet-2 text-graphite')} title={entry.billingNote ?? undefined}>
          {t('entry.billedShort', { hours: h(entry.billedMinutes) })}
        </span>
      )}
      {entry.perm.editBilling && (
        <button type="button" className="inline-flex items-center gap-0.5 rounded px-1 text-accent hover:bg-accent-soft" onClick={() => onEdit(entry)} title={t('entry.billedTitle')}>
          <Pencil className="size-3" />
          {!differs && t('entry.billedAction')}
        </button>
      )}
    </span>
  );
}

/** A Monday: gives the weekday names (Mo, Di ...) in the language of the app for the day buttons. */
const WEEK_REFERENCE = '2026-10-05';

export type RecurringRule = ProcOutput<'recurring.list'>['rules'][number];

export interface EntryDialogProps {
  open: boolean;
  onClose: () => void;
  entry?: EntryView | null;
  defaultDate?: string;
  defaultTask?: PickedTask | null;
  personId?: string;
  /** a recurring entry of the person to change (the dialog shows the rule instead of a single entry) */
  rule?: RecurringRule | null;
  /** at the right edge of wide screens, so the week behind stays visible ("Meine Woche") */
  side?: boolean;
}

export function EntryDialog({ open, onClose, entry, defaultDate, defaultTask, personId, rule, side }: EntryDialogProps) {
  const { t } = useTranslation();
  const { me, can } = useMe();
  const toast = useToast();
  const editing = !!entry;
  const [task, setTask] = useState<PickedTask | null>(null);
  const [picking, setPicking] = useState(false);
  const [date, setDate] = useState('');
  const [minutes, setMinutes] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [billingType, setBillingType] = useState<BillingType>('billable');
  const [location, setLocation] = useState('');
  // "zählt nicht als Überstunden" (setting time.noOvertime), e.g. a voluntary training in the evening
  const [noOvertime, setNoOvertime] = useState(false);
  const [reason, setReason] = useState('');
  const [forPerson, setForPerson] = useState<string>('');
  // several days of the same task at once (own bookings only; the review step shows every day before anything is saved)
  const [multi, setMulti] = useState(false);
  const [rangeEnd, setRangeEnd] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [reviewing, setReviewing] = useState(false);
  // recurring entry (request 3): the same booking on chosen weekdays of every week, as drafts
  const [repeat, setRepeat] = useState(false);
  const [repeatEnd, setRepeatEnd] = useState('');
  const people = useQ('people.list', {}, { enabled: open && !editing && can('time.manage') });

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setTask({
        id: entry.taskId,
        ref: entry.taskRef,
        title: entry.taskTitle,
        projectNumber: entry.projectNumber,
        projectName: entry.projectName,
        defaultBillingType: entry.billingType,
        billingTypeFixed: entry.taskBillingTypeFixed,
        formaState: entry.taskFormaState,
        statusGroup: entry.taskStatusGroup,
        formaIssueId: entry.formaIssueId,
        formaProjectId: entry.formaProjectId,
        source: entry.taskSource,
      });
      setDate(entry.workDate);
      setMinutes(entry.minutes);
      setDescription(entry.description);
      setBillingType(entry.billingType);
      setLocation(entry.workLocation ?? '');
      setNoOvertime(entry.noOvertime);
      setPicking(false);
    } else if (rule) {
      const rt = rule.task;
      setTask(
        rt
          ? toPicked(rt)
          : { id: rule.taskId, ref: '', title: '', projectNumber: '', projectName: '', defaultBillingType: rule.billingType ?? 'billable', billingTypeFixed: false, formaState: null, statusGroup: 'active' },
      );
      setPicking(!rt);
      setDate(rule.startsOn);
      setMinutes(rule.minutes);
      setDescription(rule.description);
      setBillingType(rule.billingType ?? rt?.defaultBillingType ?? 'billable');
      setLocation(rule.workLocation ?? '');
      setNoOvertime(false);
      setForPerson('');
    } else {
      setTask(defaultTask ?? null);
      setPicking(!defaultTask);
      setDate(defaultDate ?? me.today);
      setMinutes(null);
      setDescription('');
      setBillingType(defaultTask?.defaultBillingType ?? 'billable');
      setNoOvertime(false);
      setLocation(lastLocation(me.settings.workLocations));
      setForPerson(personId && personId !== me.person.id ? personId : '');
    }
    setReason('');
    setMulti(false);
    setReviewing(false);
    setRepeat(!!rule);
    setRepeatEnd(rule?.endsOn ?? '');
    setWeekdays(rule ? rule.weekdays : [1, 2, 3, 4, 5]);
  }, [open, entry, rule, defaultDate, defaultTask, personId, me.today, me.person.id, me.settings.workLocations]);

  const create = useM('time.create', { invalidate: ['time.', 'tasks.recent', 'kpi.', 'approvals.', 'cockpit.'] });
  const update = useM('time.update', { invalidate: ['time.', 'tasks.recent', 'kpi.', 'approvals.', 'billing.', 'cockpit.'] });
  const saveRule = useM('recurring.save', { invalidate: ['recurring.', 'time.', 'tasks.recent', 'kpi.', 'cockpit.'] });
  const busy = create.isPending || update.isPending || saveRule.isPending;
  const error = create.error ?? update.error ?? saveRule.error;
  // billing type: the rule of task and project is the default; the person chooses for own entries when the
  // company allows it, approvers for others; a fixed rule stays fixed. Hours to invoice are set in Abrechnung.
  const mayChooseType = editing ? !!entry?.perm.setBillingType : forPerson ? can('time.manage') : me.settings.ownBillingType;

  const showWarnings = (w: string[]) => {
    for (const code of w) toast.warn(t(`entry.warnings.${code as 'WEEKEND'}`, { hours: h(me.settings.warnDailyMinutes, 0) }));
  };

  const multiAllowed = !editing && !forPerson && !repeat;
  // weekly repetition: own new bookings, or the rule that is changed
  const repeatAllowed = !!rule || (!editing && !forPerson);
  const repeatMode = repeat && repeatAllowed;
  const days = useMemo(() => (multi && multiAllowed && date ? daysOnWeekdays(date, rangeEnd || date, weekdays, MAX_DAYS) : []), [multi, multiAllowed, date, rangeEnd, weekdays]);
  const multiValid = multi && multiAllowed && !!task && !!minutes && !!date && days.length > 0 && days.length <= MAX_DAYS;
  const pre = useQ(
    'quick.precheck',
    {
      rows: task && minutes
        ? days.map((workDate) => ({ taskId: task.id, workDate, minutes, description, billingType: mayChooseType ? billingType : undefined, workLocation: location || null }))
        : [],
    },
    { enabled: open && multiValid && reviewing, gcTime: 0, staleTime: 0, placeholderData: undefined },
  );
  const review = useReview(multi && reviewing ? pre.data?.rows : undefined);
  const favs = useFavorites(open && !forPerson && (!editing || entry?.personId === me.person.id));
  const toggleRepeat = (on: boolean) => {
    setRepeat(on);
    if (on) {
      setMulti(false);
      setReviewing(false);
      setWeekdays([isoWeekday(date || me.today)]);
      if (date < weekStart(me.today)) setDate(me.today);
    }
  };
  const submitRule = async () => {
    if (!task || !minutes || !date) return;
    rememberLocation(location);
    const r = await saveRule.mutateAsync({
      id: rule?.id,
      taskId: task.id,
      weekdays,
      minutes,
      description,
      billingType: mayChooseType && !task.billingTypeFixed ? billingType : null,
      workLocation: location || null,
      startsOn: date,
      endsOn: repeatEnd || null,
      active: rule ? rule.active : true,
    });
    toast.ok(r.created ? t('recurring.savedBooked', { count: r.created }) : t('recurring.saved'));
    onClose();
  };
  const toggleMulti = (on: boolean) => {
    setMulti(on);
    setReviewing(false);
    if (on) {
      const friday = addDays(weekStart(date), 4);
      setRangeEnd(date <= friday ? friday : date);
    }
  };
  const goReview = () => {
    if (!multiValid) return;
    rememberLocation(location);
    setReviewing(true);
  };
  const bookMulti = () =>
    review
      .book()
      .then((r) => {
        if (r.failed === 0) {
          toast.ok(t('quick.doneMany', { count: r.created }));
          onClose();
        } else toast.warn(t('quick.partial', { ok: r.created, failed: r.failed }));
      })
      .catch(() => {});

  // marking hours that do not count as extra hours: setting on; editing needs the right on the entry
  const canMarkNoOvertime = !!me.settings.noOvertime && (!entry || !editing || entry.perm.setNoOvertime);
  const byCategory = !!entry && editing && entry.noOvertimeByCategory;
  // editing: only changed parts are sent
  const entryDirty =
    !!entry &&
    (task?.id !== entry.taskId ||
      date !== entry.workDate ||
      minutes !== entry.minutes ||
      description !== entry.description ||
      (mayChooseType && billingType !== entry.billingType) ||
      (canMarkNoOvertime && noOvertime !== entry.noOvertime) ||
      (location || null) !== (entry.workLocation || null));

  const removeM = useM('time.delete', { invalidate: ['time.', 'tasks.recent', 'kpi.', 'approvals.', 'cockpit.'] });
  const resubmitM = useM('time.submit', { invalidate: ['time.', 'approvals.', 'kpi.'] });
  const submit = async (again: boolean) => {
    if (!task || !minutes || !date) return;
    rememberLocation(location);
    if (editing && entry) {
      let warnings: string[] = [];
      if (entryDirty) {
        const before = entry;
        const r = await update.mutateAsync({
          id: entry.id,
          version: entry.version,
          taskId: task.id !== entry.taskId ? task.id : undefined,
          workDate: date,
          minutes,
          description,
          billingType: mayChooseType && billingType !== entry.billingType ? billingType : undefined,
          workLocation: location || null,
          noOvertime: canMarkNoOvertime && noOvertime !== entry.noOvertime ? noOvertime : undefined,
          reason: reason || undefined,
        });
        warnings = r.warnings;
        // undo: the values before the change (the entry keeps its history; a reason is given where one is required)
        toast.undoable(t('common.saved'), t('common.undo'), () =>
          update
            .mutateAsync({
              id: before.id,
              version: r.entry.version,
              taskId: r.entry.taskId !== before.taskId ? before.taskId : undefined,
              workDate: before.workDate,
              minutes: before.minutes,
              description: before.description,
              billingType: r.entry.billingType !== before.billingType && r.entry.perm.setBillingType ? before.billingType : undefined,
              workLocation: before.workLocation || null,
              noOvertime: r.entry.noOvertime !== before.noOvertime && r.entry.perm.setNoOvertime ? before.noOvertime : undefined,
              reason: r.entry.perm.editNeedsReason ? t('common.undoReason') : undefined,
            })
            // an own submitted entry went back to draft with the change: undoing submits it again
            .then((x) => (before.status === 'submitted' && x.entry.status === 'draft' ? resubmitM.mutateAsync({ ids: [before.id] }) : undefined))
            .then(() => toast.ok(t('common.undone')))
            .catch(toast.error),
        );
      } else toast.ok(t('common.saved'));
      showWarnings(warnings);
      onClose();
      return;
    }
    const r = await create.mutateAsync({
      taskId: task.id,
      workDate: date,
      minutes,
      description,
      billingType: mayChooseType ? billingType : undefined,
      workLocation: location || null,
      noOvertime: canMarkNoOvertime && noOvertime ? true : undefined,
      personId: forPerson || undefined,
    });
    // undo of a new booking: it is deleted again (with a reason when it was booked for somebody else)
    toast.undoable(t('common.saved'), t('common.undo'), () =>
      removeM
        .mutateAsync({ id: r.entry.id, reason: r.entry.perm.deleteNeedsReason ? t('common.undoReason') : undefined })
        .then(() => toast.ok(t('common.undone')))
        .catch(toast.error),
    );
    showWarnings(r.warnings);
    if (again) {
      setMinutes(null);
      setDescription('');
      setTask(null);
      setPicking(true);
    } else onClose();
  };

  const needsReason = editing && entry?.perm.editNeedsReason && entryDirty;
  const fixed = task?.billingTypeFixed;
  const valid = !!task && !!minutes && !!date && (!needsReason || reason.trim().length >= 3) && (!editing || entryDirty);
  const ruleValid = repeatMode && !!task && !!minutes && !!date && weekdays.length > 0 && (!repeatEnd || repeatEnd >= date);
  const link = task ? formaLink(me.settings.deepLinkTemplate, task.formaProjectId, task.formaIssueId) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide={picking}
      side={side}
      id="entry"
      title={rule ? t('recurring.editTitle') : editing ? t('entry.editTitle') : t('entry.newTitle')}
      footer={
        repeatMode ? (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="primary" icon={<Repeat className="size-4" />} disabled={!ruleValid} loading={busy} onClick={() => submitRule().catch(() => {})}>
              {t('recurring.save')}
            </Button>
          </>
        ) : multi && multiAllowed ? (
          reviewing ? (
            <>
              <Button onClick={() => setReviewing(false)}>{t('quick.back')}</Button>
              <Button variant="primary" disabled={review.count === 0} loading={review.pending} onClick={bookMulti}>
                {t('quick.bookN', { count: review.count })}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>{t('common.cancel')}</Button>
              <Button variant="primary" disabled={!multiValid} onClick={goReview}>
                {t('quick.checkDays')}
              </Button>
            </>
          )
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            {!editing && (
              <Button disabled={!valid} loading={busy} onClick={() => submit(true).catch(() => {})}>
                {t('entry.saveAndNew')}
              </Button>
            )}
            <Button variant="primary" disabled={!valid} loading={busy} onClick={() => submit(false).catch(() => {})}>
              {t('common.save')}
            </Button>
          </>
        )
      }
    >
      {multi && multiAllowed && reviewing && task ? (
        <div className="flex flex-col gap-3">
          <div className="rounded-md border border-line bg-sheet-2 px-3 py-2 text-sm">
            <span className="num font-semibold text-accent">{task.ref}</span> <span className="font-medium">{task.title}</span>
            <span className="num ml-2 text-graphite">
              {h(minutes)} h · {t('quick.daysCount', { count: days.length })}
            </span>
            <TaskMeta task={task} />
          </div>
          <p className="text-xs text-graphite">{t('quick.reviewIntro')}</p>
          {pre.isLoading && <Loading />}
          <ErrorBox error={pre.error} />
          {pre.data && <PlanReview review={review} />}
          <ErrorBox error={review.error} />
        </div>
      ) : (
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (repeatMode) {
            if (ruleValid) submitRule().catch(() => {});
          } else if (multi && multiAllowed) goReview();
          else if (valid) submit(false).catch(() => {});
        }}
      >
        {entry?.status === 'rejected' && entry.rejectReason && <Notice tone="bad">{t('week.rejectedHint', { reason: entry.rejectReason })}</Notice>}
        {!editing && !rule && can('time.manage') && (
          <Field label={t('entry.bookFor')}>
            <Select value={forPerson} onChange={(e) => setForPerson(e.target.value)}>
              <option value="">{me.person.displayName}</option>
              {(people.data?.people ?? [])
                .filter((p) => p.id !== me.person.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        <Field label={t('entry.task')}>
          {task && !picking ? (
            <div className="flex items-start justify-between gap-2 rounded-md border border-line bg-sheet-2 px-3 py-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 text-sm">
                  <span className="num font-semibold text-accent">{task.ref}</span>
                  <span className="font-medium">{task.title}</span>
                  <TaskBadges formaState={task.formaState} statusGroup={task.statusGroup} source={task.source} internal={task.projectInternal} />
                </div>
                <TaskMeta task={task} />
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {link && (
                  <FormaIssueLink href={link} className="rounded p-1.5 text-graphite hover:bg-sheet hover:text-ink" title={t('task.openInForma')}>
                    <ExternalLink className="size-4" />
                  </FormaIssueLink>
                )}
                {favs.enabled && <FavoriteStar on={favs.ids.has(task.id)} disabled={favs.pending} onToggle={() => favs.toggle(task.id)} />}
                <Button size="sm" onClick={() => setPicking(true)}>
                  {t('entry.changeTask')}
                </Button>
              </div>
            </div>
          ) : (
            <TaskPicker
              autoFocus
              personId={forPerson || (editing ? entry?.personId : undefined)}
              onPick={(x) => {
                setTask(x);
                setPicking(false);
                if (x.billingTypeFixed || !editing) setBillingType(x.defaultBillingType);
              }}
            />
          )}
        </Field>
        <div className="flex flex-wrap gap-4">
          <Field label={multi && multiAllowed ? t('quick.from') : repeatMode ? t('recurring.from') : t('entry.date')} htmlFor="entry-date">
            <Input
              id="entry-date"
              type="date"
              min={repeatMode && !rule ? weekStart(me.today) : undefined}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                if (rangeEnd && rangeEnd < e.target.value) setRangeEnd(e.target.value);
              }}
              className="w-40"
            />
          </Field>
          {multi && multiAllowed && (
            <Field label={t('quick.to')} htmlFor="entry-date-to">
              <Input id="entry-date-to" type="date" min={date} value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="w-40" />
            </Field>
          )}
          {repeatMode && (
            <Field label={t('recurring.until')} hint={t('recurring.untilHint')} htmlFor="entry-repeat-end">
              <Input id="entry-repeat-end" type="date" min={date} value={repeatEnd} onChange={(e) => setRepeatEnd(e.target.value)} className="w-40" />
            </Field>
          )}
          <Field label={multi && multiAllowed ? t('quick.hoursPerDay') : repeatMode ? t('recurring.hoursPerDay') : t('entry.hours')} hint={t('entry.hoursHint', { step: me.settings.stepMinutes })} htmlFor="entry-hours">
            <HoursInput id="entry-hours" value={minutes} onChange={setMinutes} />
          </Field>
          <Field label={t('entry.location')} className="min-w-48 flex-1" htmlFor="entry-location">
            <WorkLocationSelect id="entry-location" value={location} onChange={setLocation} />
          </Field>
        </div>
        {(multiAllowed || repeatAllowed) && (
          <div className="flex flex-col gap-2">
            {multiAllowed && <Checkbox label={t('quick.multi')} hint={multi ? undefined : t('quick.multiHint')} checked={multi} onChange={(e) => toggleMulti(e.target.checked)} />}
            {repeatAllowed && !rule && !multi && (
              <Checkbox label={t('recurring.toggle')} hint={repeat ? t('recurring.toggleOnHint') : t('recurring.toggleHint')} checked={repeat} onChange={(e) => toggleRepeat(e.target.checked)} />
            )}
            {((multi && multiAllowed) || repeatMode) && (
              <div className={clsx('flex flex-wrap items-center gap-x-4 gap-y-2', !rule && 'pl-6')}>
                <div role="group" aria-label={t('quick.weekdays')} className="flex flex-wrap gap-1">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                    const on = weekdays.includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setWeekdays(on ? weekdays.filter((x) => x !== n) : [...weekdays, n])}
                        className={clsx('rounded-md border px-2.5 py-1 text-xs font-medium', on ? 'border-accent bg-accent-soft text-accent' : 'border-line text-graphite hover:bg-sheet-2')}
                      >
                        {wd(addDays(WEEK_REFERENCE, n - 1))}
                      </button>
                    );
                  })}
                </div>
                {repeatMode ? (
                  <span className={clsx('text-xs', weekdays.length === 0 ? 'text-bad' : 'text-graphite')}>{weekdays.length === 0 ? t('quick.daysNone') : t('recurring.everyWeek')}</span>
                ) : (
                  <span className={clsx('num text-xs', days.length === 0 || days.length > MAX_DAYS ? 'text-bad' : 'text-graphite')}>
                    {days.length === 0 ? t('quick.daysNone') : days.length > MAX_DAYS ? t('quick.daysTooMany', { max: MAX_DAYS }) : t('quick.daysCount', { count: days.length })}
                  </span>
                )}
              </div>
            )}
            {repeatMode && <p className="text-xs text-graphite">{t('recurring.explain')}</p>}
          </div>
        )}
        <Field label={t('entry.description')} hint={t('entry.descriptionHint')} htmlFor="entry-desc">
          <Textarea id="entry-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
        </Field>
        {mayChooseType && !fixed ? (
          <Field label={t('entry.billingType')} hint={t(`billingType.${billingType}Hint`)}>
            <Select value={billingType} onChange={(e) => setBillingType(e.target.value as BillingType)}>
              {BILLING_TYPES.map((b) => (
                <option key={b} value={b}>
                  {t(`billingType.${b}`)}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          task && (
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-graphite">
              <span>{t('entry.billingType')}:</span>
              <Pill>{t(`billingType.${billingType}`)}</Pill>
              <span>{fixed ? t('billingType.fixed') : t('entry.billingTypeByRule')}</span>
            </p>
          )
        )}
        {repeatMode ? null : byCategory ? (
          <p className="text-xs text-graphite">{t('entry.noOvertimeByCategory')}</p>
        ) : (
          canMarkNoOvertime && <Checkbox label={t('entry.noOvertime')} hint={t('entry.noOvertimeHint')} checked={noOvertime} onChange={(e) => setNoOvertime(e.target.checked)} />
        )}
        {needsReason && (
          <Field label={t('common.reason')} hint={t('common.reasonHint')}>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
        <ErrorBox error={error} />
        <button type="submit" hidden />
      </form>
      )}
    </Modal>
  );
}

/** Change history of one entry (audit log). */
export function HistoryModal({ entryId, onClose }: { entryId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useQ('time.history', { id: entryId ?? '' }, { enabled: !!entryId });
  const fmt = (v: unknown) => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${x === null ? '-' : String(x)}`).join(', ');
    return String(v);
  };
  return (
    <Modal open={!!entryId} onClose={onClose} title={t('common.history')}>
      {q.data && (
        <ol className="flex flex-col gap-3 text-sm">
          {q.data.events.map((e) => (
            <li key={e.id} className="border-b border-line pb-2">
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-medium">{t(`entry.audit.${e.action as 'time.update'}`, { defaultValue: e.action })}</span>
                <span className="num text-xs text-graphite">
                  {dt(e.at)} · {e.actorName ?? '-'}
                </span>
              </div>
              {e.reason && <div className="text-graphite">{e.reason}</div>}
              {(e.before !== null || e.after !== null) && (
                <div className="num mt-1 text-xs text-graphite">
                  {fmt(e.before)} → {fmt(e.after)}
                </div>
              )}
            </li>
          ))}
          <li className="num text-xs text-graphite">
            {t('common.createdAt')} {dt(q.data.created.at)}
          </li>
        </ol>
      )}
    </Modal>
  );
}

/** Action buttons for one entry, based on the permissions the server computed. */
export function EntryActions({ entry, onEdit, compact }: { entry: EntryView; onEdit: (e: EntryView) => void; compact?: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [confirm, setConfirm] = useState<'delete' | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const inv = { invalidate: ['time.', 'approvals.', 'kpi.', 'tasks.recent'] };
  const del = useM('time.delete', inv);
  const restore = useM('time.restore', inv);
  const submit = useM('time.submit', inv);
  const withdraw = useM('time.withdraw', inv);
  const p = entry.perm;
  return (
    <div className={clsx('flex items-center', compact ? 'gap-0' : 'gap-0.5')}>
      {p.edit && !entry.deletedAt && <IconButton label={t('common.edit')} icon={<Pencil className="size-3.5" />} onClick={() => onEdit(entry)} />}
      {p.submit && !compact && (
        <IconButton
          label={t('entry.submit')}
          icon={<Send className="size-3.5" />}
          onClick={() =>
            submit
              .mutateAsync({ ids: [entry.id] })
              .then((r) => r.submitted && toast.undoable(t('entry.submittedDone', { count: r.submitted }), t('common.undo'), () => withdraw.mutateAsync({ ids: r.ids }).catch(toast.error)))
              .catch(toast.error)
          }
        />
      )}
      {p.withdraw && !compact && (
        <IconButton
          label={t('entry.withdraw')}
          icon={<Undo2 className="size-3.5" />}
          onClick={() =>
            withdraw
              .mutateAsync({ ids: [entry.id] })
              .then((r) => r.withdrawn && toast.ok(t('entry.withdrawnDone', { count: r.withdrawn })))
              .catch(toast.error)
          }
        />
      )}
      {p.delete && <IconButton label={t('common.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => setConfirm('delete')} />}
      {p.restore && entry.deletedAt && (
        <IconButton label={t('entry.restore')} icon={<RotateCcw className="size-3.5" />} onClick={() => restore.mutateAsync({ id: entry.id }).then(() => toast.ok(t('common.saved'))).catch(toast.error)} />
      )}
      {!compact && <IconButton label={t('common.history')} icon={<History className="size-3.5" />} onClick={() => setHistory(entry.id)} />}
      <ConfirmModal
        open={confirm === 'delete'}
        onClose={() => setConfirm(null)}
        title={t('entry.deleteTitle')}
        text={`${d(entry.workDate)} · ${entry.taskRef} ${entry.taskTitle} · ${hh(entry.minutes)}. ${t('entry.deleteText')}`}
        confirmLabel={t('common.delete')}
        danger
        reason={p.deleteNeedsReason ? 'required' : undefined}
        loading={del.isPending}
        error={del.error}
        onConfirm={(reason) =>
          del
            .mutateAsync({ id: entry.id, reason: reason || undefined })
            .then(() => {
              toast.undoable(t('common.deleted'), t('common.undo'), () =>
                restore
                  .mutateAsync({ id: entry.id })
                  .then(() => toast.ok(t('entry.restored')))
                  .catch(toast.error),
              );
              setConfirm(null);
            })
            .catch(() => {})
        }
      />
      <HistoryModal entryId={history} onClose={() => setHistory(null)} />
    </div>
  );
}

export function EntryStatusLine({ entry }: { entry: EntryView }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <StatusPill status={entry.status} />
      <TaskBadges formaState={entry.taskFormaState} statusGroup={entry.taskStatusGroup} />
    </span>
  );
}

export function SelectAllCheckbox({ ids, selected, setSelected }: { ids: string[]; selected: Set<string>; setSelected: (s: Set<string>) => void }) {
  const { t } = useTranslation();
  const all = ids.length > 0 && ids.every((i) => selected.has(i));
  return (
    <Checkbox
      label={<span className="sr-only">{t('common.selectAll')}</span>}
      checked={all}
      onChange={() => setSelected(all ? new Set() : new Set(ids))}
    />
  );
}
